import React, { useState, useMemo } from "react"
import { LinkIcon } from "components/SvgIcons"
import { Button, createIcon } from "@chakra-ui/react"
import { Spinner } from "components/Spinner"
import { useLocale } from "hooks"
import { getUrl } from "components/Layouts/Header/Header.utils"
import { useRouter } from "lib/router"
import { fonts } from "theme/themeConfig"
import { colours } from "@shared/ui.design-system"

type Props = {
  advocateCode?: string
}

export const CopyButton: React.FC<Props> = ({ advocateCode }) => {
  const [isCopied, setIsCopied] = useState(false)
  const [isCopying, setIsCopying] = useState(false)

  const locale = useLocale()

  const { asPath } = useRouter()

  const linkToCopy = useMemo(() => {
    if (!advocateCode) {
      return getUrl(locale, asPath)
    }
    const params = new URLSearchParams()
    params.set("redirectUrl", asPath)
    params.set("referralCode", advocateCode)
    params.set("utm_source", "advocate")
    params.set("utm_medium", "PDP")
    params.set("utm_campaign", "link")

    return `${getUrl(locale, "/redeem-referral")}?${decodeURIComponent(
      params.toString()
    )}`
  }, [advocateCode, locale, asPath])

  const handleCopy = () => {
    setIsCopying(true)

    navigator.clipboard
      .writeText(linkToCopy)
      .then(() => {
        setIsCopied(true)
        setTimeout(() => {
          setIsCopied(false)
        }, 2000) // Reset "Copied" text after 2 seconds
      })
      .catch((error) => {
        console.error("Error copying to clipboard:", error)
      })
      .finally(() => {
        setIsCopying(false)
      })
  }
  const iconSizes = { h: "1.8rem", w: "1.8rem" }
  return (
    <Button
      onClick={handleCopy}
      border="1.5px solid black"
      width="full"
      variant="secondary"
      fontFamily={fonts.acuminLight}
      leftIcon={
        isCopied || isCopying ? (
          <CheckIcon color={colours.primary.dark} {...iconSizes} />
        ) : (
          <LinkIcon strokeWidth="10px" {...iconSizes} />
        )
      }
    >
      {isCopying ? (
        <Spinner color={colours.primary.dark} />
      ) : (
        <span>{isCopied ? "Copied" : "Copy Link"}</span>
      )}
    </Button>
  )
}

const CheckIcon = createIcon({
  displayName: "Check",
  viewBox: "0 0 21 20",
  defaultProps: {
    role: "img",
  },
  path: (
    <>
      <title> Check Icon </title>
      <svg
        width="21"
        height="20"
        viewBox="0 0 21 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M17.3693 5.11516C17.5369 5.27514 17.5443 5.54197 17.3858 5.71114L8.80704 14.868C8.72954 14.9507 8.62219 14.9983 8.50941 14.9998C8.39663 15.0014 8.288 14.9569 8.20825 14.8764L3.62233 10.2474C3.45922 10.0828 3.45922 9.81583 3.62233 9.65119C3.78544 9.48655 4.04989 9.48655 4.21299 9.65119L8.49522 13.9736L16.7789 5.1318C16.9374 4.96263 17.2017 4.95518 17.3693 5.11516Z"
          fill="currentColor"
        />
      </svg>
    </>
  ),
})
